import { useEffect, useRef, useState } from 'react';
import type {
  ExamPreviewOfficialComment,
  OfficialCommentBody,
} from '../../domain/entities/content.entity';
import { useContentItem } from '../hooks/use-content-items';
import { useSetQuestionOfficialComment } from '../hooks/use-exams';
import {
  type AttachedImage,
  attachedFilenames,
  editorHtmlToTokens,
  openImageFilePicker,
  toDataUri,
  toImagePayload,
  tokensToEditorHtml,
} from '../question-images';
import { preflightChosenFile, preparedToBase64 } from '../image-preflight';
import { getApiErrorMessage } from '../display';
import { RichTextEditor, type PickedImage } from './RichTextEditor';

/**
 * O que o card usa para incluir o comentário no seu próprio "Salvar".
 * Mesmo padrão do `buildRef` do `BodyForm`: o campo é dono do seu estado, e o
 * pai só pergunta "está sujo?" e manda salvar.
 */
export interface OfficialCommentHandle {
  isDirty: () => boolean;
  /**
   * **Propaga** a falha em vez de devolvê-la como `false` — ver o mesmo
   * contrato (e o porquê) em `ClassificationHandle`. A mensagem inline
   * continua saindo daqui pelo `save.error`.
   */
  save: () => Promise<void>;
}
export type OfficialCommentRef = { current: OfficialCommentHandle | null };

/**
 * Comentário oficial da questão dentro do form de edição — **um por questão**,
 * então não há lista, escolha nem "adicionar outro": é um campo.
 *
 * Duas coisas que não são óbvias e não podem mudar:
 *
 * **1. O texto é buscado cru, não reaproveitado do preview.** No preview cada
 * `{{img:<fileId>}}` já virou `<img src="…">`, e `img` não está no allowlist de
 * escrita do back — salvar aquele HTML apagaria as imagens do comentário. É a
 * mesma armadilha que já obriga o corpo da questão a rebuscar o item.
 *
 * **2. Salvar acontece pelo botão do card, não por um botão daqui.** Um segundo
 * "Salvar" dentro do card aberto criaria a dúvida de qual salva o quê — e a
 * guarda de alterações não salvas passaria a ter dois estados para conciliar.
 *
 * **3. Autor não se digita.** Quem salva assina, e o servidor resolve isso do
 * token — pedir o nome num campo seria pedir para digitar o que ele já sabe, e
 * abrir espaço para digitar errado. O crédito atual aparece como texto; ao
 * salvar, passa a ser de quem salvou. (Na importação por CSV é diferente: lá o
 * nome vem da planilha, porque quem sobe a prova pode estar importando o
 * comentário de outra pessoa do time.)
 */
export function OfficialCommentField({
  questionId,
  comment,
  imageUrlsByFileId,
  onDirtyChange,
  fieldRef,
}: {
  questionId: string;
  /** O que o preview trouxe: id, autor e o texto **renderizado**. */
  comment: ExamPreviewOfficialComment | null;
  imageUrlsByFileId: Record<string, string>;
  onDirtyChange: (dirty: boolean) => void;
  fieldRef: OfficialCommentRef;
}) {
  const save = useSetQuestionOfficialComment(questionId);
  // Só busca quando já existe comentário — questão sem comentário não paga
  // requisição nenhuma por abrir o form.
  const raw = useContentItem(comment?.id);

  const [text, setText] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [issue, setIssue] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(comment === null);

  // O texto cru chega depois (é outra requisição). Semeia uma vez: um `useState`
  // com valor inicial não veria a resposta, e sincronizar a cada render
  // sobrescreveria o que a pessoa já digitou.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !raw.data) return;
    seeded.current = true;
    const body = raw.data.item.body as OfficialCommentBody;
    setText(tokensToEditorHtml(body.text ?? '', imageUrlsByFileId));
    setLoaded(true);
  }, [raw.data, imageUrlsByFileId]);

  const initial = useRef('');
  useEffect(() => {
    if (loaded && !initial.current && text) {
      initial.current = text;
    }
    // Roda no momento em que o valor inicial fica conhecido; depois disso, o
    // que muda é o rascunho, não a referência.
  }, [loaded, text]);

  const dirty = loaded && text !== initial.current;

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const pickImage = async (): Promise<PickedImage | null> => {
    const file = await openImageFilePicker();
    if (!file) return null;
    const result = await preflightChosenFile(file.name, [], file);
    if ('issue' in result) {
      setIssue(result.issue.message);
      return null;
    }
    setIssue(null);
    const base64 = await preparedToBase64(result.prepared);
    const previewUrl = toDataUri(base64, result.prepared.mime);
    setImages((prev) => {
      const byName = new Map(prev.map((image) => [image.filename, image]));
      byName.set(file.name, { filename: file.name, base64, previewUrl });
      return [...byName.values()];
    });
    return { filename: file.name, src: previewUrl };
  };

  const commit = async (): Promise<void> => {
    const tokens = editorHtmlToTokens(text);
    await save.mutateAsync({
      text: tokens,
      // Só sobe o que o texto cita — imagem cujo nó foi apagado não vai, e o
      // back recusaria "arquivo sem token" se fosse.
      images: toImagePayload(images, attachedFilenames([tokens])),
    });
    // Só depois de gravado: com o `initial` movido antes, uma falha deixaria o
    // campo dizendo "sem alteração" com a alteração ainda por salvar.
    initial.current = text;
  };

  // O card chama estes dois no seu próprio `save()`/guarda.
  fieldRef.current = { isDirty: () => dirty, save: commit };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-(--color-text-secondary) text-sm font-medium">
          Comentário oficial
        </span>
        <span className="text-(--color-text-secondary) text-xs">
          {comment === null
            ? 'esta questão ainda não tem — escreva para criar'
            : comment.author
              ? `por ${comment.author} — salvar passa o crédito para você`
              : 'sem autor — salvar credita você'}
        </span>
      </div>

      {/* O gate é `loaded`, NÃO `raw.isLoading`: o `RichTextEditor` só lê
          `value` no mount (o TipTap guarda `content` uma vez), e a semeadura do
          texto acontece num efeito — ou seja, um render DEPOIS de `isLoading`
          virar `false`. Montar aqui pelo `isLoading` montava o editor com
          string vazia e o `setText` seguinte não tinha mais como entrar: o
          campo abria EM BRANCO numa questão que tem comentário, e a primeira
          tecla digitada substituía o comentário inteiro ao salvar. `loaded`
          vira `true` no mesmo efeito (e no mesmo batch) que semeia o texto,
          então o editor nasce já com o conteúdo certo. */}
      {comment !== null && raw.isError ? (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(
            raw.error,
            'Não foi possível carregar o comentário desta questão.',
          )}{' '}
          Feche e abra a edição para tentar de novo — salvar agora apagaria o
          texto que está gravado.
        </p>
      ) : !loaded ? (
        <p className="text-(--color-text-secondary) text-sm">
          Carregando o comentário…
        </p>
      ) : (
        <>
          <RichTextEditor
            label=""
            value={text}
            onChange={setText}
            onPickImage={pickImage}
          />
          {/* Apagar o texto e salvar remove o comentário — dizer isso aqui
              evita a pergunta "como eu tiro esse comentário?". */}
          {comment !== null && (
            <p className="text-(--color-text-secondary) text-xs">
              Apagar todo o texto e salvar remove o comentário desta questão.
            </p>
          )}
        </>
      )}

      {issue && (
        <p className="text-sm text-red-600" role="alert">
          {issue}
        </p>
      )}
      {save.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(save.error, 'Falha ao salvar o comentário')}
        </p>
      )}
    </div>
  );
}
